/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import './ShareCardSummaryCategoryTitle.styles.scss';

import HealthEntryIcons from '../../../components/icons/HealthEntryIcons';
import HealthEntryCategoryNames from '../../../../constants/HealthEntryCategoryNames';
import IconFile from '../../../components/icons/IconFile';

import ShareCardIncludedTypeNames from '../../../../constants/ShareCardIncludedTypeNames';
import IconNote from '../../../components/icons/IconNote';
import { Icons } from '../../../../bphComponents/bundle';
import COLORS from '../../../styles/Colors';

const iconProps = {
  color: 'dark-neutral',
  colorClassName: 'gsMedDark',
  size: '24',
};

const healthEntryInfo = {
  [HealthEntryCategoryNames.condition]: {
    icon: <HealthEntryIcons.IconCondition {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.healthEntry.conditions.name', values: { digit1: 2 } }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  [HealthEntryCategoryNames.medication]: {
    icon: <HealthEntryIcons.IconMedication {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.healthEntry.medications.name', values: { digit1: 2 } }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  [HealthEntryCategoryNames.otherTreatment]: {
    icon: <HealthEntryIcons.IconTreatment {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.healthEntry.treatments.name', values: { digit1: 2 } }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  [HealthEntryCategoryNames.procedure]: {
    icon: <HealthEntryIcons.IconProcedure {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.healthEntry.procedures.name', values: { digit1: 2 } }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  [HealthEntryCategoryNames.allergy]: {
    icon: <HealthEntryIcons.IconAllergy {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.healthEntry.allergies.name', values: { digit1: 2 } }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  [HealthEntryCategoryNames.vaccination]: {
    icon: <HealthEntryIcons.IconVaccination {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.healthEntry.vaccinations.name', values: { digit1: 2 } }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  [HealthEntryCategoryNames.lab]: {
    icon: <HealthEntryIcons.IconLab {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.lab.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.mostRecentValue.text', values: { string1 } };
      },
    },
    [ShareCardIncludedTypeNames.all]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.lab.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.allValues.text', values: { string1 } };
      },
    },
  },
  [HealthEntryCategoryNames.measurement]: {
    icon: <HealthEntryIcons.IconMeasurement {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.measurements.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.mostRecentValue.text', values: { string1 } };
      },
    },
    [ShareCardIncludedTypeNames.all]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.measurements.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.allValues.text', values: { string1 } };
      },
    },
  },
  [HealthEntryCategoryNames.geneticResult]: {
    icon: <HealthEntryIcons.IconGeneticResult {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.genes.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.mostRecentValue.text', values: { string1 } };
      },
    },
    [ShareCardIncludedTypeNames.all]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.genes.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.allValues.text', values: { string1 } };
      },
    },
  },
  files: {
    icon: <IconFile.Medium {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.backpackSecondaryNav.files.name', values: {} }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  notes: {
    icon: <IconNote.Medium {...iconProps} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: () => ({ id: 'list.backpackSecondaryNav.notes.name', values: {} }),
    },
    [ShareCardIncludedTypeNames.all]: {},
  },
  contacts: {
    icon: <Icons.IconProvidersContacts.Medium className="fill-color-gsMedDark" color="blue" />,
    [ShareCardIncludedTypeNames.all]: {
      getTitlePhraseData: () => ({ id: 'shareCard.contacts.heading', values: {} }),
    },
  },
  providerContacts: {
    icon: <Icons.IconProvidersContacts.Medium className="fill-color-gsMedDark" color="blue" />,
    [ShareCardIncludedTypeNames.all]: {
      getTitlePhraseData: () => ({ id: 'shareCard.providers.heading', values: {} }),
    },
  },
  trackables: {
    icon: <Icons.IconTracking.Medium color={COLORS.gsMedDark} />,
    [ShareCardIncludedTypeNames.latest]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.trackables.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.mostRecentValue.text', values: { string1 } };
      },
    },
    [ShareCardIncludedTypeNames.all]: {
      getTitlePhraseData: (intl) => {
        const string1 = intl.formatMessage({ id: 'list.healthEntry.trackables.name' }, { digit1: 2 });
        return { id: 'share.shareCardCategory.allValues.text', values: { string1 } };
      },
    },
  },
};

const ShareCardSummaryCategoryTitle = (props) => {
  const info = healthEntryInfo[props.categoryName];
  return (
    <div className="cmp-sharecardsummary-categorytitle font-caption-1">
      <div>
        {info.icon}
      </div>
      <div>
        <FormattedMessage {...info[props.includedValues].getTitlePhraseData(props.intl)} />
      </div>
    </div>
  );
};
ShareCardSummaryCategoryTitle.propTypes = {
  categoryName: PropTypes.string.isRequired,
  intl: PropTypes.object,
  includedValues: PropTypes.oneOf([ShareCardIncludedTypeNames.all, ShareCardIncludedTypeNames.latest]),
};
ShareCardSummaryCategoryTitle.defaultProps = {
  includedValues: ShareCardIncludedTypeNames.latest,
};
export default injectIntl(ShareCardSummaryCategoryTitle);
